// Providers
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';

// Types
import {
  BaseWidgetProps,
  INormalizedTennisCompetitionDetails,
  INormalizedTennisMatch,
  ITennisTournamentProgrammeConfig,
  ITournament,
  RoundFilter,
} from '../../../../../packages/types';
import { FIXTURES, ProgrammeFilterEnums, RESULTS } from '../../../../../packages/constants/programmeStatus';
import { useEffect, useMemo, useState } from 'react';
import { useIsSmartphoneDevice } from '../../../../../packages/hooks/useIsSmartphoneDevice';
import useTournamentProgramme from '../../hooks/useTennisTournamentProgramme';
import { normalizeTennisTournamentProgrammeResponseData } from '../../normalizers/normalizeTennisTournamentProgrammeData';
import {
  defaultHandleCompetitionClick,
  defaultHandleEventClick,
  defaultHandleTournamentClick,
  extractOnlyDateSDKOptions,
} from '@sportal365/smp-widget-utils';
import { Sports } from '../../../../../packages/constants/sportType';
import { GroupSectionTitle, Header, ConfigContext, PillTabs, TextBlock, Tournament, SizeProvider } from '@velinsi/smp-widgets-ui';
import { normalizeCompetitionData } from '../../normalizers/normalizeCompetitionData';
import EventRowWithCompetition from '../../components/EventSection/EventRowWithCompetition';
import useTennisCompetition from '../../hooks/useTennisCompetition';
import { normalizeCompetitionDetailsData } from '../../normalizers/normalizeTennisCompetitionData';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';

export type TennisTournamentProgrammeWidgetProps = BaseWidgetProps<ITennisTournamentProgrammeConfig>;

export const TennisTournamentProgrammeWidget = ({
  themes,
  config,
  subscribers,
  shadow,
  sdkOptions,
  cuid,
}: TennisTournamentProgrammeWidgetProps) => {
  const showHeader = config?.dataHeaderDisplay === null || config?.dataHeaderDisplay === true;
  const [seasonId, setSeasonId] = useState<string | undefined>(config?.dataSeason);
  const [roundIds, setRoundIds] = useState<RoundFilter[] | undefined>(config?.dataRoundsFilter);

  const [selectedFilter, setSelectedFilter] = useState(config?.dataHeaderDefaultOption?.toUpperCase() || FIXTURES);
  const [isResultsDisabled, setIsResultsDisabled] = useState(false);
  const [isFixturesDisabled, setIsFixturesDisabled] = useState(false);
  const [disableAllTabs, setDisableAllTabs] = useState(false);

  const { ref } = useRootNodeRef();
  const { isMobile } = useIsSmartphoneDevice(ref);

  const {
    showWidget,
    handleClick,
    widgetData: { programme, isLoading, noPastEvents, noUpcomingEvents },
  } = useTournamentProgramme({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    dataTournament: config?.dataTournament,
    dataCompetition: config?.dataCompetition,
    filter: selectedFilter,
    seasonYear: seasonId,
    dataLimit: config?.dataLimit,
    dataDateFrom: config?.dataDateFrom,
    dataDateTo: config?.dataDateTo,
    dataRoundsFilter: roundIds,
    dataSortDirectionFixtures: config?.dataSortDirectionFixtures,
    dataSortDirectionResults: config?.dataSortDirectionResults,
    dataOddsDisplay: config?.dataOddsDisplay,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataOddsMarket: config?.dataOddsMarket,
    dataOddsMarketValueType: config?.dataOddsMarketValueType,
    dataWidgetCuid: cuid,
    dataGameType: config?.dataGameType,
    dataGender: config?.dataGender,
  });

  const { competition, seasonYears } = useTennisCompetition({
    dataCompetition: config?.dataCompetition,
    season: seasonId,
  });

  useEffect(() => {
    if (noPastEvents && noUpcomingEvents) {
      setDisableAllTabs(true);
    }

    if (!isLoading) {
      if ((!programme || programme?.length < 1) && !disableAllTabs) {
        if (selectedFilter === FIXTURES) {
          setSelectedFilter(RESULTS);
          setIsFixturesDisabled(true);
          setIsResultsDisabled(false);
        } else if (selectedFilter === RESULTS) {
          setSelectedFilter(FIXTURES);
          setIsFixturesDisabled(false);
          setIsResultsDisabled(true);
        }
      } else if (disableAllTabs && programme?.length < 1) {
        setIsFixturesDisabled(true);
        setIsResultsDisabled(true);
        setDisableAllTabs(false);
      }
    }
  }, [programme]);

  const normalizedSeasonYears = useMemo(() => {
    return normalizeCompetitionDetailsData({
      competition,
      seasonYears,
      dataEntityLinks: config?.dataEntityLinks,
    });
  }, [competition, config, seasonYears]) as INormalizedTennisCompetitionDetails;

  const options = useMemo(
    () =>
      normalizedSeasonYears?.seasons &&
      normalizedSeasonYears.seasons.map((season) => ({
        label: season.name,
        value: season.id,
      })),
    [normalizedSeasonYears?.seasons],
  );

  const tournamentProgramme = useMemo(
    () =>
      normalizeTennisTournamentProgrammeResponseData({
        programme,
        dataEntityLinks: config?.dataEntityLinks,
        dataShortStatusType: config?.dataShortStatusType,
        dataLabels: config?.dataLabels,
        sdkDateOptions: extractOnlyDateSDKOptions(sdkOptions),
      }),
    [programme, config, sdkOptions],
  );

  const normalizedCompetition = useMemo(
    () =>
      normalizeCompetitionData({
        competition,
        dataEntityLinks: config?.dataEntityLinks,
      }),
    [competition, config],
  );

  if (!showWidget) return null;

  const handleHeaderCompetitionClick = () =>
    defaultHandleTournamentClick(handleClick, normalizedCompetition?.id || '', normalizedCompetition?.name);

  const handleEventClick = (event: string | number) => defaultHandleEventClick(handleClick, event);

  const handleCompetitionClick = (competition: ITournament) => defaultHandleCompetitionClick(handleClick, competition);

  const handleSeasonChange = (value?: string) => {
    setDisableAllTabs(false);
    setRoundIds([]);
    setSeasonId(value);
    setIsFixturesDisabled(false);
    setSelectedFilter(FIXTURES);
  };

  const filters = [
    { name: config?.dataLabels?.fixtures || ProgrammeFilterEnums.FIXTURES, value: FIXTURES, disabled: isFixturesDisabled },
    { name: config?.dataLabels?.results || ProgrammeFilterEnums.RESULTS, value: RESULTS, disabled: isResultsDisabled },
  ];

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <div ref={ref}>
        <ThemeProvider
          isLoading={isLoading}
          shadow={shadow}
          themes={themes}
          dataTheme={config?.dataTheme}
          css={{ backgroundColor: '$primaryBackgroundColor' }}
        >
          <SizeProvider>
            <Header
              logo={normalizedCompetition?.logo}
              link={normalizedCompetition?.link}
              urlTarget={normalizedCompetition?.urlTarget}
              title={normalizedCompetition?.name}
              options={options}
              selectedOption={seasonId}
              handleChange={handleSeasonChange}
              handleClick={handleHeaderCompetitionClick}
              handleSubscriptionClick={handleClick}
              displayMainHeader={showHeader}
              secondaryHeader={
                config?.dataOptionsDisplay ? (
                  <PillTabs
                    tabs={filters}
                    selectedTab={selectedFilter}
                    handleTabClick={(tab) => {
                      setSelectedFilter(tab);
                    }}
                    handleSubscriptionClick={handleClick}
                  />
                ) : undefined
              }
            />
            {!tournamentProgramme || tournamentProgramme?.length < 1 ? (
              <TextBlock text={config?.dataLabels?.no_matches} />
            ) : (
              tournamentProgramme?.map(({ tournament, matches }) => (
                <div key={tournament?.id}>
                  <Tournament
                    isMainImageRound
                    sport={Sports.TENNIS}
                    tournament={tournament}
                    handleTournamentClick={handleCompetitionClick}
                  />
                  {matches?.map(({ matches, round }, index) => (
                    <div key={`${round?.name} + ${index}`}>
                      <GroupSectionTitle title={round?.name} />
                      <EventRowWithCompetition
                        sport={Sports.TENNIS}
                        key={round?.name}
                        isMobile={isMobile}
                        competition={tournament}
                        showOdds={config?.dataOddsDisplay}
                        showCompetition={false}
                        showRoundName={false}
                        matches={matches as INormalizedTennisMatch[]}
                        handleClick={handleClick}
                        handleCompetitionClick={handleCompetitionClick}
                        handleEventClick={handleEventClick}
                        allowInlineOdds={config?.dataAllowInlineOdds}
                      />
                    </div>
                  ))}
                </div>
              ))
            )}
          </SizeProvider>
        </ThemeProvider>
      </div>
    </ConfigContext.Provider>
  );
};
